import type {IApiErrorMetadata} from '@stripe-internal/connect-embedded-lib';
import {
  handleFetchResponse,
  getFormURLEncodedBody,
  getStripeOrGraphQLErrorMetadata,
} from '@stripe-internal/connect-embedded-lib';
import {getDevLogger} from '../utils/getLogger';
import type {RiskSignalsCollectionDataLayerResponse} from './types';
import type {
  IFrameCollectRiskSignalsRequest,
  IFrameCollectRiskSignalsResponse,
} from '../data-layer-client/FrameMessenger';
import type {RiskSignalCollectionErrorType} from '../connect/ConnectJSInterface/RiskSignalCollectionTypes';
import {authStore} from './authentication/AuthenticationStore';
import {getCurrentScriptUrlContext} from '../utils/getCurrentScriptUrlContext';
import {getStripeApiHost} from '../data-layer-client/buildStripeClient';
import {getDataLayerHeaders} from './proxyRequest';

const devLogger = getDevLogger();

/**
 * Collect and send risk signals to Stripe via the /v1/risk_signals endpoint
 * @param request {IFrameCollectRiskSignalsRequest} The data layer request
 * @returns {IFrameCollectRiskSignalsResponse} The data layer response
 */
export async function collectAndSendRiskSignals(
  request: IFrameCollectRiskSignalsRequest,
): Promise<IFrameCollectRiskSignalsResponse> {
  devLogger.info('Data Layer: collectAndSendRiskSignals called');

  try {
    // Send risk signals to Stripe
    await sendRiskSignalsToStripe();

    // Build data layer response
    const response: RiskSignalsCollectionDataLayerResponse = {
      status: 'completed',
    };

    return {
      id: request.id,
      type: request.type,
      requestType: 'collectRiskSignalsResponse',
      response,
    };
  } catch (error) {
    devLogger.error('Error in collectAndSendRiskSignals:', error);

    // Map error to the correct RiskSignalCollectionErrorType
    const mappedError = mapToRiskSignalCollectionError(error);

    return {
      id: request.id,
      type: request.type,
      requestType: 'collectRiskSignalsResponse',
      response: {
        status: 'failed',
        error: mappedError,
      },
    };
  }
}

/**
 * Send risk signals to Stripe via the /v1/risk_signals endpoint
 * @returns The risk signals response from Stripe
 */
async function sendRiskSignalsToStripe() {
  // Get API host
  const {serviceEnvironment} = getCurrentScriptUrlContext();
  const apiHost = getStripeApiHost(serviceEnvironment);

  // Make request to /v1/accounts endpoint
  devLogger.info('Posting risk signals to /v1/risk_signals', {
    apiHost,
  });

  // Get API key
  const apiKey = authStore.getApiKey('account_session');
  if (!apiKey) {
    throw new Error('No valid account session apiKey found');
  }

  // Build headers
  const headers: HeadersInit = getDataLayerHeaders({
    Authorization: `Bearer ${apiKey.key}`,
    'Content-Type': 'application/x-www-form-urlencoded',
  });

  const riskSignalsUrl = `https://${apiHost}/v1/risk_signals`;
  const fetchResponse = await fetch(riskSignalsUrl, {
    method: 'POST',
    headers,
    body: getFormURLEncodedBody({
      // TODO (@justinrao: 2025-12-31) add hcaptcha and iovation data
    }),
  });

  return handleFetchResponse(fetchResponse);
}

/**
 * Maps errors to RiskSignalCollectionError (excluding connect.js loading errors)
 */
function mapToRiskSignalCollectionError(error: unknown): {
  type: RiskSignalCollectionErrorType;
  message?: string;
} {
  // follow the same pattern used in auth error mapping
  // with what's applicable to risk signal collection
  const e = getStripeOrGraphQLErrorMetadata(error);

  const getErrorType = (e: IApiErrorMetadata) => {
    if (e.stripeErrorCode === 'Failed to fetch') {
      return 'api_connection_error';
    }
    if (e.stripeStatusCode === 429) {
      return 'rate_limit_error';
    }
    if (e.stripeStatusCodeRange === '4xx') {
      return 'invalid_request_error';
    }
    // default to api_error
    return 'api_error';
  };

  const errorType = getErrorType(e);
  return {type: errorType, message: e.stripeErrorMessage};
}
